import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [state, setState] = useState(token ? "ready" : "invalid"); // ready | invalid | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setState("done");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="reset-password-card">
        <div className="flex justify-center mb-6"><Logo /></div>
        {state === "invalid" && (
          <div className="text-center space-y-3" data-testid="reset-invalid">
            <h1 className="text-xl font-display font-bold text-slate-800">Reset link invalid</h1>
            <p className="text-sm text-slate-500">This password-reset link is invalid or has expired. Please request a new one from the sign-in page.</p>
            <Button variant="outline" onClick={() => navigate("/login")} data-testid="reset-back-to-login">Back to sign in</Button>
          </div>
        )}
        {state === "done" && (
          <div className="text-center space-y-3" data-testid="reset-done-screen">
            <CheckCircle2 className="mx-auto text-brand" size={44} />
            <h1 className="text-xl font-display font-bold text-slate-800">Password updated</h1>
            <p className="text-sm text-slate-500">You can now sign in with your new password. Redirecting…</p>
          </div>
        )}
        {state === "ready" && (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-center">
              <h1 className="text-xl font-display font-bold text-slate-800">Choose a new password</h1>
              <p className="text-sm text-slate-500 mt-1">Enter a new password for your CaterCTRL account.</p>
            </div>
            <div><Label>New password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" className="mt-1.5" placeholder="At least 8 characters" /></div>
            <div><Label>Confirm password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-password-confirm" className="mt-1.5" /></div>
            <Button type="submit" disabled={busy} data-testid="reset-password-submit" className="w-full bg-brand hover:bg-brand-hover">{busy ? <Loader2 className="animate-spin" size={16} /> : "Update password"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
